import { BeneficiaryType } from '@seapoint/server/enums';
import pluralize from 'pluralize';
import { useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Select } from '../../../../design-system/select/select';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView, PayrollView } from '../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { getBankAccountName } from '../../../../utils/get-bank-account-name';
import { AccountSelectItem } from '../../../payments/components/account-select-item';
import { canInitiateBulkPayments } from '../../../payments/utils/capability-checks';
import { PayrollPaymentRecordCard } from './payroll-payment-record-card';

type BulkPayrollPaymentFormProps = {
  payroll: PayrollView;
  accounts: AccountView[];
  isLoading: boolean;
  onSubmit: (paymentDetails: BulkPayrollPaymentDetails) => void;
};

export type BulkPayrollPaymentDetails = {
  sourceAccountId: string;
  payrollId: string;
  totalAmount: number;
};

export const BulkPayrollPaymentForm = ({ accounts, payroll, isLoading, onSubmit }: BulkPayrollPaymentFormProps) => {
  const availableSourceAccounts = accounts.filter(
    (account) => canInitiateBulkPayments(account) && payroll.currency === account.currency,
  );
  const defaultSourceAccountId = availableSourceAccounts[0]?.id;
  const [selectedSourceAccountId, setSelectedSourceAccountId] = useState(defaultSourceAccountId);
  const selectedSourceAccount = availableSourceAccounts.find((a) => a.id === selectedSourceAccountId);

  const totalAmount = payroll.records.reduce((acc, record) => acc + record.netPay, 0);
  const hasInsufficientFunds = selectedSourceAccount && totalAmount > selectedSourceAccount.balance;

  const handleSubmit = () => {
    if (!selectedSourceAccount) return;

    const paymentDetails: BulkPayrollPaymentDetails = {
      sourceAccountId: selectedSourceAccount.id,
      payrollId: payroll.id,
      totalAmount,
    };

    onSubmit(paymentDetails);
  };

  return (
    <div className="w-full">
      <Stack space={6} align="stretch">
        {/* From Section */}
        <Stack space={2} align="stretch">
          <p className="text-sm font-medium text-seapoint-gray">From</p>
          <div
            className={`rounded-lg border bg-white ${
              hasInsufficientFunds ? 'border-seapoint-red' : 'border-seapoint-gray-border'
            }`}
          >
            <Select
              placeholder={`Select ${payroll.currency} Account`}
              onValueChange={(value) => {
                setSelectedSourceAccountId(value);
              }}
              defaultValue={selectedSourceAccountId}
              triggerClassName="py-3 px-4 rounded-lg border-0 bg-white text-black text-base font-medium"
              isTriggerFullWidth={true}
            >
              {availableSourceAccounts.map((account) => {
                const accountName = getBankAccountName({
                  displayName: account.displayName,
                  institutionName: account.institution?.name,
                });

                return (
                  <Select.Item key={account.id} value={account.id} className="py-3">
                    <AccountSelectItem
                      balance={account.balance}
                      currency={account.currency}
                      institutionLogo={account.institution?.logo}
                      name={accountName}
                      fspAdapter={account.fspAdapter}
                    />
                  </Select.Item>
                );
              })}
            </Select>
          </div>
          {hasInsufficientFunds && <p className="text-sm font-medium text-seapoint-red">Not enough funds on account</p>}
        </Stack>

        {/* To Section */}
        <Stack space={2} align="stretch">
          <Inline justify="between" align="center">
            <p className="text-sm font-medium text-seapoint-gray">To</p>
            <p className="text-sm font-medium text-seapoint-gray">
              {payroll.records.length} {pluralize('recipient', payroll.records.length)}
            </p>
          </Inline>

          <div className="max-h-64 overflow-y-auto">
            <Stack space={4} align="stretch">
              {payroll.records.map((record) => (
                <PayrollPaymentRecordCard
                  key={record.id}
                  name={record.employee.firstName + ' ' + record.employee.lastName}
                  currency={payroll.currency}
                  accountDetails={
                    record.employee.destination.type === BeneficiaryType.IBAN
                      ? {
                          beneficiaryType: BeneficiaryType.IBAN,
                          iban: record.employee.destination.iban!,
                        }
                      : {
                          beneficiaryType: BeneficiaryType.SCAN,
                          accountNumber: record.employee.destination.accountNumber!,
                          sortCode: record.employee.destination.sortCode!,
                        }
                  }
                  reference={payroll.reference}
                  amount={record.netPay}
                />
              ))}
            </Stack>
          </div>
        </Stack>

        {/* Total Net Pay */}
        <div className="rounded-lg border border-seapoint-gray-border bg-white p-4">
          <Inline justify="between" align="center">
            <p className="text-base font-medium text-black">Total net pay</p>
            <p className="text-xl font-medium text-black">
              {getAmountWithCurrency({
                amount: totalAmount,
                currency: payroll.currency,
                isAccountBalance: true,
              })}
            </p>
          </Inline>
        </div>

        {/* Pay Button */}
        <Button
          variant="default"
          size="lg"
          onClick={handleSubmit}
          disabled={!selectedSourceAccount || isLoading || hasInsufficientFunds}
          isLoading={isLoading}
          className="w-full bg-seapoint-yellow text-black hover:bg-seapoint-yellow/90"
        >
          Pay
        </Button>
      </Stack>
    </div>
  );
};
